<template>
    <UModal :model-value="open" @update:model-value="$emit('update:open', $event)">
        <UCard :ui="{ rounded: 'rounded-lg', body: { base: 'overflow-visible' } }">
            <template #header>
                <div class="flex items-center justify-between">
                    <h3 class="text-base font-medium leading-6 text-gray-800">
                        Transfer Call
                    </h3>
                    <UButton color="gray" variant="ghost" icon="i-ph-x-bold" class="-my-1" @click="dismiss()" />
                </div>
            </template>
            <div class="">
                <div v-if="loading" class="relative flex flex-col items-center justify-center w-full p-4 text-center">
                    <UIcon name="i-ph-circle-dashed" class="size-6 text-white animate-spin-slow" />
                    <h3 class="mt-2 text-sm text-gray-400 font-mono">Loading users...</h3>
                </div>
                <div v-else-if="!users.length" class="relative block w-full rounded-lg border-2 border-dashed border-gray-300 p-8 text-center">
                    <UIcon name="i-ph-user-duotone" class="size-7 text-gray-400" />
                    <h3 class="mt-2 text-base font-medium text-gray-900">No active users</h3>
                    <p class="mt-1 text-sm text-gray-500">Please try again later.</p>
                </div>
                <div v-else class="shadow-sm rounded-md">
                    <button
                        v-for="(user, index) in users" 
                        :key="user.id" 
                        @click="onTransfer(user)"
                        type="button"
                        class="-mt-px relative w-full py-4 px-5 border border-gray-300 hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary transition-colors"
                        :class="{
                            'rounded-t-md': index == 0,
                            'rounded-b-md': index == users.length - 1
                        }"
                    >
                        <div class="flex items-center space-x-3">
                            <div class="flex-1 flex items-center justify-between space-x-3">
                                <div>
                                    <p class="leading-5 font-medium text-gray-900">
                                        {{ user.name }}
                                    </p>
                                    <p v-if="user.metadata?.extension" class="text-xs font-medium text-gray-600">
                                        <span class="leading-none text-base text-green-500">&bull;</span> Extension: {{ user.metadata.extension }}
                                    </p>
                                </div>
                                <BadgeUserRole v-if="user.role" :role="user.role" />
                            </div>
                            
                            <UIcon v-if="confirming && selectedUserId == user.id" name="i-ph-circle-notch" class="size-5 text-gray-500 animate-spin-slow" />
                            <UIcon v-else-if="!confirming" name="i-ph-caret-right-bold" class="size-3 text-gray-400" />
                        </div>
                    </button>
                </div>
            </div>
        </UCard>
    </UModal>
</template>

<script setup lang="ts">
import { UserRole, type User } from '~/types/user'

const emit = defineEmits<{
    'update:open': [boolean],
    'transferred': [void]
}>()

const props = defineProps<{
    open: boolean
    callId?: string
}>()

const auth = useAuthStore()
const client = useSupabaseClient()
const toast = useToast()

const loading = ref<boolean>(false)
const confirming = ref<boolean>(false)
const users = ref<Partial<User>[]>([])
const selectedUserId = ref<string>()

watch(() => props.open, (open) => {
    if (open) init()
})

async function init() {
    loading.value = true

    const { data, error } = await client.from('users')
        .select('id, name, role, metadata')
        .not('id', 'eq', auth.user?.id)
        .not('login_at', 'is', null)
        .is('logout_at', null)
        .is('deleted_at', null)
        .in('role', [UserRole.medicalDirector, UserRole.doctor, UserRole.operator])

    if (data) {
        users.value = data
    } else if (error) {
        console.log(error)
    }

    loading.value = false
}

async function onTransfer(user: Partial<User>) {
    if (props.callId) {
        selectedUserId.value = user.id
        confirming.value = true

        try {
            const { error } = await client.from('calls')
                .update({
                    transferrer_id: auth.user?.id,
                    user_id: user.id
                } as never)
                .eq('id', props.callId)

            if (error) {
                throw error
            }

            toast.add({
                title: 'Call transferred successfully!',
                color: 'green',
                icon: 'i-ph-check-circle-duotone'
            })
            
            emit('transferred')
        } catch (e) {
            toast.add({
                title: 'Could not transfer call.',
                color: 'red',
                icon: 'i-ph-warning-duotone'
            })

            console.error('Could not transfer call: ' + e)
        } finally {
            dismiss()
        }
    }
}

function dismiss() {
    emit('update:open', false)
}
</script>